import { useState } from "react";
import { CheckCircle2, Info, MapPin, Pencil, Plus, PowerOff } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorState } from "@/components/shared/ErrorState";
import { EmptyState } from "@/pages/CustomerPortal/Financial/component/EmptyState";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { Address, ClientAddressType } from "@/lib/api";
import { formatCep } from "@/lib/formatters";
import { AddressDialog } from "./AddressDialog";
import { AddressBook } from "./useAddressBook";

export interface AddressSection {
  type: ClientAddressType;
  title: string;
  description: string;
}

interface AddressListViewProps {
  book: AddressBook;
  /** Seções por finalidade, com os textos do dono (CNPJ ou pessoa física). */
  sections: AddressSection[];
  /** Aviso na seção de entrega (ex.: como o endereço é usado no pedido). */
  shippingHint?: string;
  /** Texto do diálogo de cadastro/edição; ausente usa o do cliente CNPJ. */
  dialogDescription?: string;
  /** "Usar o mesmo endereço para entrega" no cadastro de cobrança. */
  allowSameForShipping?: boolean;
  errorMessage: string;
}

const formatAddress = (address: Address) =>
  [
    [address.street, address.number].filter(Boolean).join(", "),
    address.complement,
    address.district,
    `${address.city}/${address.state}`,
    `CEP ${formatCep(address.postcode)}`,
  ]
    .filter(Boolean)
    .join(" · ");

/** Endereços agrupados por finalidade, com selo de padrão e as ações de cada endereço (CNPJ e pessoa física). */
export function AddressListView({
  book,
  sections,
  shippingHint,
  dialogDescription,
  allowSameForShipping = false,
  errorMessage,
}: AddressListViewProps) {
  const {
    addresses,
    isLoading,
    isError,
    refetch,
    createAddress,
    updateAddress,
    setDefault,
    deactivate,
    isSaving,
    pendingAddressId,
  } = book;
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [dialog, setDialog] = useState<{ open: boolean; address?: Address | null; type: ClientAddressType }>({
    open: false,
    type: "Billing",
  });
  const [showInactive, setShowInactive] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4" aria-busy="true">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError) {
    return <ErrorState message={errorMessage} onRetry={() => void refetch()} />;
  }

  const handleDeactivate = async (address: Address) => {
    const confirmed = await confirm({
      title: "Desativar endereço?",
      description: `${formatAddress(address)}. O endereço deixa de aparecer para uso, mas o histórico é mantido.`,
      confirmLabel: "Desativar",
      variant: "destructive",
    });
    if (confirmed) deactivate(address.id);
  };

  const inactiveCount = addresses.filter((address) => !address.isActive).length;

  return (
    <>
      <ConfirmDialog />
      <div className="space-y-6">
        {sections.map((section) => {
          const items = addresses.filter(
            (address) => address.addressType === section.type && (address.isActive || showInactive),
          );
          return (
            <Card key={section.type} className="border-border shadow-lg">
              <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
                <div className="space-y-1">
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5" aria-hidden="true" />
                    {section.title}
                  </CardTitle>
                  <CardDescription>{section.description}</CardDescription>
                </div>
                <Button
                  size="sm"
                  onClick={() => setDialog({ open: true, address: null, type: section.type })}
                >
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  Novo endereço
                </Button>
              </CardHeader>
              <CardContent className="space-y-3">
                {section.type === "Shipping" && shippingHint && (
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription>{shippingHint}</AlertDescription>
                  </Alert>
                )}
                {items.length === 0 ? (
                  <EmptyState
                    title="Nenhum endereço cadastrado"
                    message="Cadastre um endereço para esta finalidade."
                    className="py-6"
                  />
                ) : (
                  <ul className="space-y-3">
                    {items.map((address) => {
                      const isPending = pendingAddressId === address.id;
                      return (
                        <li
                          key={address.id}
                          className="flex flex-col gap-3 rounded-lg border border-border p-4 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="space-y-1">
                            <div className="flex flex-wrap items-center gap-2">
                              {address.label && <span className="font-medium">{address.label}</span>}
                              {address.isDefault && (
                                <Badge variant="success">
                                  <CheckCircle2 className="mr-1 h-3 w-3" aria-hidden="true" />
                                  Padrão
                                </Badge>
                              )}
                              {!address.isActive && <Badge variant="secondary">Desativado</Badge>}
                            </div>
                            <p className="text-sm">{formatAddress(address)}</p>
                          </div>
                          {address.isActive && (
                            <div className="flex flex-wrap gap-2">
                              {!address.isDefault && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={isPending}
                                  onClick={() => setDefault(address.id)}
                                >
                                  Tornar padrão
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={isPending}
                                onClick={() => setDialog({ open: true, address, type: address.addressType })}
                                aria-label={`Editar endereço ${formatAddress(address)}`}
                              >
                                <Pencil className="h-4 w-4" aria-hidden="true" />
                              </Button>
                              {!address.isDefault && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={isPending}
                                  onClick={() => void handleDeactivate(address)}
                                  aria-label={`Desativar endereço ${formatAddress(address)}`}
                                >
                                  <PowerOff className="h-4 w-4" aria-hidden="true" />
                                </Button>
                              )}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          );
        })}

        {inactiveCount > 0 && (
          <Button variant="link" className="px-0" onClick={() => setShowInactive((value) => !value)}>
            {showInactive ? "Ocultar endereços desativados" : `Mostrar endereços desativados (${inactiveCount})`}
          </Button>
        )}
      </div>

      <AddressDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((current) => ({ ...current, open }))}
        address={dialog.address}
        addressType={dialog.type}
        isSubmitting={isSaving}
        onCreate={createAddress}
        onUpdate={(addressId, address) => updateAddress({ addressId, address })}
        description={dialogDescription}
        allowSameForShipping={allowSameForShipping}
      />
    </>
  );
}
